import { useCallback, useState } from 'react';
import { AccountExternalApi } from 'src/api';
import { CircolanteItem, OrdinatoItem } from 'src/api/AccountExternalApi';

const unwrapResult = (result: PromiseSettledResult<any>, itemType: string) => ({
	data:
		result.status === 'fulfilled'
			? result.value?.map((item) =>
					Object.defineProperty({ ...item }, '$type', {
						value: itemType,
						enumerable: false,
					})
			  )
			: [],
	error: result.status === 'rejected' ? new Error(result.reason) : undefined,
});

type HookOptions = {
	fetchOnMount: boolean;
};

type Result<T> = {
	error?: Error;
	data: T[];
};

export function useAccountExternalApi(
	accountId: string,
	options: HookOptions = { fetchOnMount: false }
) {
	const [loadingState, setIsLoading] = useState({
		isLoadingAll: false,
		isLoadingOrdinato: false,
		isLoadingCircolante: false,
	});

	const [ordinato, setOrdinato] = useState<Result<OrdinatoItem>>({
		error: undefined,
		data: [],
	});
	const [circolante, setCircolante] = useState<Result<CircolanteItem>>({
		error: undefined,
		data: [],
	});

	const retrieveExternalData = useCallback(async () => {
		try {
			setIsLoading((prev) => ({ ...prev, isLoadingAll: true }));
			const [ordinatoResult, circolanteResult] = await Promise.allSettled([
				AccountExternalApi.ordinato(accountId),
				AccountExternalApi.circolante(accountId),
			]);

			setOrdinato(unwrapResult(ordinatoResult, 'ordinato'));
			setCircolante(unwrapResult(circolanteResult, 'circolante'));
		} catch (error) {
			console.warn(error);
			setOrdinato((prev) => ({ ...prev, error: error as Error }));
			setCircolante((prev) => ({ ...prev, error: error as Error }));
		} finally {
			setIsLoading((prev) => ({ ...prev, isLoadingAll: false }));
		}
	}, [accountId]);

	const refreshOrdinato = useCallback(async () => {
		try {
			setIsLoading((prev) => ({ ...prev, isLoadingOrdinato: true }));
			const [ordinatoResult] = await Promise.allSettled([
				AccountExternalApi.ordinato(accountId),
			]);
			setOrdinato(unwrapResult(ordinatoResult, 'ordinato'));
		} catch (error) {
			console.warn(error);
			setOrdinato((prev) => ({ ...prev, error: error as Error }));
		} finally {
			setIsLoading((prev) => ({ ...prev, isLoadingOrdinato: false }));
		}
	}, [accountId]);

	const refreshCircolante = useCallback(async () => {
		try {
			setIsLoading((prev) => ({ ...prev, isLoadingCircolante: true }));
			const [circolanteResult] = await Promise.allSettled([
				AccountExternalApi.circolante(accountId),
			]);
			setCircolante(unwrapResult(circolanteResult, 'circolante'));
		} catch (error) {
			console.warn(error);
			setCircolante((prev) => ({ ...prev, error: error as Error }));
		} finally {
			setIsLoading((prev) => ({ ...prev, isLoadingCircolante: false }));
		}
	}, [accountId]);

	return {
		retrieveExternalData,
		ordinato,
		circolante,
		loadingState,
		actions: { refreshOrdinato, refreshCircolante },
	};
}
